import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import {
  useElements,
  useStripe,
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement,
} from "@stripe/react-stripe-js";
import { useNavigate } from "react-router-dom";
import CopyRightTextMain from "../../../components/CopyRightTextMain";
import SuccessModal from "../../../components/SuccessModal";
import EditIcon from "../../../assets/images/Edit.svg";
import { baseFetch } from "../../../helpers/apiClient";
import { formatPrice } from "../../../helpers/currency";
import { useDispatch, useSelector } from "react-redux";
import { toggleSessionModal } from "../../../redux/sessionSlice";
import { fetchProfileInfo } from "../../../redux/profileSlice";
import PlanSelectionModal from "../../../components/PlanSelectionModal";
import "../../../modules/Auth/EnterSecurityCode.css";
import DiscountApplyModal from "../../../components/DiscountAppyModal";
import BillingProvinceSelect from "../../../components/BillingProvinceSelect";
import RoundedTextField from "../../../components/RoundedPaymentTextField";
const createOptions = () => ({
  style: {
    base: {
      fontSize: "16px",
      fontWeight: "500",
      color: "#2A4876",
      backgroundColor: "#F1F7FA",
      padding: "12px 16px",
      borderRadius: "15px",
      "::placeholder": {
        color: "#989899",
        fontSize: "16px",
      },
    },
    invalid: {
      color: "#fa755a",
    },
  },
  placeholder: "Enter here",
});

const EditPaymentCard = ({
  selectedPlan = "monthly",
  selectedPackage,
  handleClosePayScreen,
  setSelectedPlan,
}) => {
  const [values, setValues] = useState({
    cardholderName: "",
    cardNumber: false,
    cvv: false,
    expiry: false,
  });
  const [loading, setLoading] = useState(false);
  const [subsError, setSubsError] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [discountClicked, setDiscountClicked] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [openPlansModal, setOpenPlansModal] = useState(false);
  const [priceData, setPriceData] = useState(null);
  let profile = useSelector((state) => state?.profileSlice?.profile);
  const [openDiscountModal, setOpenDiscountModal] = useState(false);
  const [step, setStep] = useState(1);
  const [billingDetails, setBillingDetails] = useState({
    name: "",
    email: "",
    phone: "",
    line1: "",
    city: "",
    state: "",
    postal_code: "",
    country: "CA",
  });
  const [taxInfo, setTaxInfo] = useState(null);
  const previewRequest = useRef(null);
  const couponRequest = useRef(null);

  useEffect(() => () => previewRequest.current?.abort(), []);
  useEffect(
    () => () => couponRequest.current?.abort(),
    [selectedPlan, selectedPackage],
  );

  const startPreview = () => {
    previewRequest.current?.abort();
    previewRequest.current = new AbortController();
    return previewRequest.current.signal;
  };

  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const activePack = useSelector(
    (state) => state?.profileSlice?.profile?.subscription?.active,
  );

  // useEffect(() => {
  //   const cardNumberElement = elements?.getElement(CardNumberElement)
  //   const cvvElement = elements?.getElement(CardCvcElement)
  //   const expiryElement = elements?.getElement(CardExpiryElement)

  //   const handleElementChange = (element, field) => {
  //     subsError && setSubsError('')
  //     setValues((prev) => ({ ...prev, [field]: element?.complete }))
  //   }

  //   cardNumberElement?.on('change', (e) => handleElementChange(e, 'cardNumber'))
  //   cvvElement?.on('change', (e) => handleElementChange(e, 'cvv'))
  //   expiryElement?.on('change', (e) => handleElementChange(e, 'expiry'))
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [elements])

  const handleStripeChange = (field) => (event) => {
    subsError && setSubsError("");
    setValues((prev) => ({ ...prev, [field]: event.complete }));
  };

  useEffect(() => {
    if (activePack && step === 2) {
      previewUpgradePrices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePack, selectedPlan, step]);

  useEffect(() => {
    if (step === 2 && !activePack) {
      fetchTaxInfo();
    }
  }, [selectedPlan, activePack]);

  const fetchTaxInfo = async (couponCode) => {
    const signal = startPreview();
    const payload = {
      purchaseType: "subscription",
      priceId:
        selectedPlan === "monthly"
          ? selectedPackage?.prices[0]?.priceId
          : selectedPackage.prices[1]?.priceId ||
            selectedPackage.prices[0]?.priceId,
      address: {
        line1: billingDetails?.line1,
        line2: "",
        city: billingDetails?.city,
        state: billingDetails?.state,
        country: billingDetails?.country,
        postal_code: billingDetails?.postal_code,
      },
    };
    if (couponCode) {
      payload["couponCode"] = couponCode;
    }
    try {
      const resp = await baseFetch({
        url: "/v1/web/subscription/tax-preview",
        method: "POST",
        body: payload,
        dispatch,
        signal,
      });
      if (signal.aborted) return;
      setTaxInfo(resp?.data);
      setSubsError("");
      setStep(2);
    } catch (errMsg) {
      if (signal.aborted) return;
      setSubsError(errMsg);
    }
  };

  const previewUpgradePrices = (discountCode, discountAmt) => {
    const signal = startPreview();
    const payload = {
      newPriceId:
        selectedPlan === "monthly"
          ? selectedPackage?.prices[0]?.priceId
          : selectedPackage.prices[1]?.priceId ||
            selectedPackage.prices[0]?.priceId,
    };
    if (discountCode) {
      payload["couponCode"] = discountCode;
    }
    baseFetch({
      url: "/v1/web/subscription/preview-upgrade-details",
      method: "POST",
      body: payload,
      dispatch,
      signal,
    })
      .then((resp) => {
        if (signal.aborted) return;
        const updatedSubtotalAmt = discountCode
          ? resp?.data?.previewDetails?.amount_due + discountAmt
          : resp?.data?.previewDetails?.amount_due;
        const updatedTotalAmt = resp?.data?.previewDetails?.amount_due;
        const updatedObj = {
          subtotal: updatedSubtotalAmt,
          total: updatedTotalAmt,
        };
        setTaxInfo(resp?.data?.previewDetails?.taxDetails);
        setPriceData((prev) => {
          return { ...updatedObj, discount: prev?.discount };
        });
        setLoading(false);
      })
      .catch((err) => {
        if (signal.aborted) return;
        setSubsError(err);
        setLoading(false);
        if (err === "Please authenticate") {
          dispatch(toggleSessionModal(false));
        }
      });
  };

  const handleChange = (name, value) => {
    subsError && setSubsError("");
    if (name === "cardholderName") {
      if (/^[A-Za-z\s]+$/.test(value) || value === "") {
        setValues((prev) => ({ ...prev, [name]: value }));
      }
    }
  };

  const finalTotal = (
    (discountClicked || priceData
      ? Number(priceData?.total) / 100
      : selectedPlan === "monthly" || !selectedPlan
        ? selectedPackage?.prices[0]?.unit_amount / 100
        : selectedPackage?.prices[1]?.unit_amount / 100) +
    (taxInfo?.taxAmount || 0) / 100
  ).toFixed(2);

  const handleSubmit = async () => {
    setLoading(true);

    if (!stripe || !elements) {
      setLoading(false);
      return;
    }
    const { postal_code, city, state, country, line1 } = billingDetails;
    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: "card",
      card: elements.getElement(CardNumberElement),
      billing_details: {
        address: {
          country,
          city,
          state,
          line1,
          postal_code,
        },
      },
    });

    if (error) {
      setSubsError(error.message);
      setLoading(false);
      return;
    }
    const paymentMethodId = paymentMethod?.id;

    const payload = {
      couponCode: discountCode,
      newPlanPriceId:
        selectedPlan === "monthly"
          ? selectedPackage?.prices[0]?.id
          : selectedPackage.prices[1]?.id || selectedPackage.prices[0]?.id,
    };
    if (profile.subscription?.active) {
      baseFetch({
        url: "/v1/web/subscription/upgrade-plan",
        method: "POST",
        body: payload,
        dispatch,
      })
        .then(() => {
          setLoading(false);
          dispatch(fetchProfileInfo());
          handleClosePayScreen();
        })
        .catch((err) => {
          setSubsError(err);
          setLoading(false);
          if (err === "Please authenticate") {
            dispatch(toggleSessionModal(false));
          }
        });
    } else {
      const payload = {
        couponCode: discountCode,
        productId: selectedPackage.productId,
        priceId:
          selectedPlan === "monthly"
            ? selectedPackage.prices[0]?.priceId
            : selectedPackage.prices[1]?.priceId ||
              selectedPackage.prices[0]?.priceId,
        paymentMethodId: paymentMethodId,
      };

      baseFetch({
        url: "/v1/web/subscription/purchase",
        method: "POST",
        body: payload,
        dispatch,
      })
        .then(async () => {
          setLoading(false);
          dispatch(fetchProfileInfo());
          handleClosePayScreen();
        })
        .catch((err) => {
          console.log(err);
          setSubsError(err);
          setLoading(false);
          if (err === "Please authenticate") {
            dispatch(toggleSessionModal(false));
          }
        });
    }
  };

  const handleDiscountClick = () => {
    if (discountClicked) {
      couponRequest.current?.abort();
      if (activePack) {
        previewUpgradePrices();
      } else {
        fetchTaxInfo();
      }
      setDiscountClicked(false);
      setDiscountCode("");
      setPriceData(null);
    } else {
      couponRequest.current?.abort();
      const controller = new AbortController();
      couponRequest.current = controller;
      const payload = {
        priceId:
          selectedPlan === "monthly"
            ? selectedPackage?.prices[0]?.priceId
            : selectedPackage.prices[1]?.priceId ||
              selectedPackage.prices[0]?.priceId,
        couponCode: discountCode,
      };
      if (activePack) {
        payload["upgrade"] = true;
      }
      baseFetch({
        url: "/v1/web/coupon/apply",
        method: "POST",
        body: payload,
        dispatch,
        signal: controller.signal,
      })
        .then(async (response) => {
          if (controller.signal.aborted) return;
          setPriceData(response?.data);
          if (activePack) {
            const discountAmt = response?.data?.discount;
            previewUpgradePrices(discountCode, discountAmt);
          } else {
            fetchTaxInfo(discountCode);
          }
          setSubsError("");
          setDiscountClicked(true);
          setOpenDiscountModal(true);
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          console.log(err);
          setSubsError(err);
          setLoading(false);
          if (err === "Please authenticate") {
            dispatch(toggleSessionModal(false));
          }
        });
    }
  };

  const handleDiscountClose = () => {
    setOpenDiscountModal(false);
  };

  const handleBillingNext = () => {
    if (
      !billingDetails.name ||
      !billingDetails.email ||
      !billingDetails.line1 ||
      !billingDetails.city ||
      !billingDetails.state ||
      !billingDetails.postal_code
    ) {
      setSubsError("Please fill all required billing fields");
      return;
    }
    if (activePack) {
      setStep(2);
      setSubsError("");
    } else {
      fetchTaxInfo();
    }
  };

  return (
    <>
      <Backdrop open={loading} sx={{ zIndex: 1201, color: "#fff" }}>
        <CircularProgress />
      </Backdrop>

      {step === 1 ? (
        // <Box sx={{ padding: '15px', height: 'calc(100vh - 120px)', overflow: 'auto' }}>
        <Box
          className="selection-box"
          sx={{
            overflow: "auto",
            height: "calc(100vh - 120px)",
          }}
        >
          <Typography
            sx={{ fontSize: "24px", fontWeight: 700, color: "#2A4876", mb: 2 }}
          >
            Billing Address
          </Typography>

          <TextField
            fullWidth
            label="Full Name"
            value={billingDetails.name}
            onChange={(e) =>
              setBillingDetails({ ...billingDetails, name: e.target.value })
            }
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Email"
            value={billingDetails.email}
            onChange={(e) =>
              setBillingDetails({ ...billingDetails, email: e.target.value })
            }
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Phone"
            value={billingDetails.phone}
            onChange={(e) =>
              setBillingDetails({ ...billingDetails, phone: e.target.value })
            }
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Address Line 1"
            value={billingDetails.line1}
            onChange={(e) =>
              setBillingDetails({ ...billingDetails, line1: e.target.value })
            }
            sx={{ mb: 2 }}
          />

          <Box display="flex" gap={2}>
            <TextField
              fullWidth
              label="City"
              value={billingDetails.city}
              onChange={(e) =>
                setBillingDetails({ ...billingDetails, city: e.target.value })
              }
            />

            <BillingProvinceSelect
              value={billingDetails.state}
              onChange={(e) =>
                setBillingDetails({ ...billingDetails, state: e.target.value })
              }
            />
          </Box>

          <Box display="flex" gap={2} mt={2}>
            <TextField
              fullWidth
              label="Postal Code"
              value={billingDetails.postal_code}
              onChange={(e) =>
                setBillingDetails({
                  ...billingDetails,
                  postal_code: e.target.value,
                })
              }
            />

            <TextField
              fullWidth
              label="Country Code"
              value={billingDetails.country}
              onChange={(e) =>
                setBillingDetails({
                  ...billingDetails,
                  country: e.target.value,
                })
              }
            />
          </Box>

          {subsError && (
            <Typography color="error" mt={2}>
              {subsError}
            </Typography>
          )}

          <Button
            fullWidth
            variant="contained"
            onClick={handleBillingNext}
            sx={{ height: "60px", mt: 3, backgroundColor: "#2A4876" }}
          >
            Next
          </Button>
        </Box>
      ) : (
        <Box
          className="selection-box"
          sx={{
            overflow: "auto",
            height: "calc(100vh - 120px)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              marginBottom: "15px",
            }}
          >
            <input
              type="text"
              value={discountCode}
              placeholder="Enter Discount Code"
              onChange={(e) => {
                const value = e.target.value;
                if (/^[a-zA-Z0-9_-]*$/.test(value)) {
                  setDiscountCode(value);
                }
              }}
              style={{ width: "100%", border: "0", height: "60px" }}
              className="input-box input-custom"
            />
            <Button
              variant={discountClicked ? "outlined" : "contained"}
              sx={{
                textTransform: "none",
                backgroundColor: discountClicked ? "#fff" : "#2A4876", // Change color conditionally
                color: discountClicked ? "red" : "#fff",
                borderRadius: "50px",
                position: "absolute",
                height: "45px",
                right: "10px",
                top: "8px",
                width: "90px",
              }}
              onClick={handleDiscountClick}
              disabled={!discountClicked && !discountCode ? true : false}
            >
              {discountClicked ? "Remove" : "Apply"}
            </Button>
          </Box>
          {/* Plan Summary */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontSize: {
                xs: "18px",
                sm: "20px",
                md: "24px",
                lg: "26px",
                xl: "32px",
              },
              marginBottom: "12px",
              color: "#2A4876",
            }}
          >
            {selectedPackage?.name} Amount
          </Typography>
          <Box
            sx={{
              borderColor: "#2A487633",
              borderWidth: "1px",
              borderStyle: "solid",
              width: "100%",
              height: "60px",
              borderRadius: "50px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
              padding: "5px 8px",
            }}
          >
            <Typography sx={{ fontWeight: 600, color: "#2A4876" }}>
              {selectedPlan === "monthly"
                ? `$${selectedPackage.prices[0]?.unit_amount / 100}/month`
                : selectedPackage.prices[1]?.unit_amount
                  ? `$${selectedPackage.prices[1]?.unit_amount / 100}/year`
                  : `$${selectedPackage.prices[0]?.unit_amount / 100}/month`}
            </Typography>
            {selectedPackage?.name !== "Echo Access Pass" && (
              <Box
                sx={{
                  width: "42px",
                  height: "42px",
                  borderRadius: "50%",
                  backgroundColor: "#2A4876",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={() => setOpenPlansModal(!openPlansModal)}
              >
                <img src={EditIcon} />
              </Box>
            )}
          </Box>
          <div className="flex justify-between items-center mb-2">
            <Typography
              variant="body"
              sx={{
                fontWeight: 400,
                fontSize: "22px",
                color: "#2A4876",
                fontFamily: "PT Sans, sans-serif !important",
              }}
            >
              Subscription Fee
            </Typography>
            {selectedPackage?.name !== "Echo Access Pass" && (
              <Typography
                variant="body"
                sx={{
                  fontWeight: 400,
                  fontSize: "22px",
                  color: "#2A4876",
                  fontFamily: "PT Sans, sans-serif !important",
                }}
              >
                $
                {formatPrice(
                  (discountClicked || priceData
                    ? priceData?.subtotal
                    : selectedPlan === "monthly"
                      ? selectedPackage?.prices[0]?.unit_amount
                      : selectedPackage?.prices[1]?.unit_amount) / 100,
                  true,
                )}
              </Typography>
            )}
            {selectedPackage?.name === "Echo Access Pass" && (
              <Typography
                variant="body"
                sx={{
                  fontWeight: 400,
                  fontSize: "22px",
                  color: "#2A4876",
                  fontFamily: "PT Sans, sans-serif !important",
                }}
              >
                $
                {formatPrice(
                  (discountClicked || priceData
                    ? priceData?.subtotal
                    : selectedPackage?.prices[0]?.unit_amount) / 100,
                  true,
                )}
              </Typography>
            )}
          </div>
          <div className="flex justify-between items-center mb-2">
            <Typography
              variant="body"
              sx={{
                fontWeight: 400,
                fontSize: "22px",
                color: "#2A4876",
                fontFamily: "PT Sans, sans-serif !important",
              }}
            >
              Tax
            </Typography>
            <Typography
              variant="body"
              sx={{
                fontWeight: 400,
                fontSize: "22px",
                color: "#2A4876",
                fontFamily: "PT Sans, sans-serif !important",
              }}
            >
              ${formatPrice((taxInfo?.taxAmount ?? 0) / 100, true)}
            </Typography>
          </div>
          {discountClicked && (
            <div className="flex justify-between items-center mb-2">
              <Typography
                variant="body"
                sx={{
                  fontWeight: 400,
                  fontSize: "22px",
                  color: "#2A4876",
                  fontFamily: "PT Sans, sans-serif !important",
                }}
              >
                Discount
              </Typography>
              <Typography
                variant="body"
                sx={{
                  fontWeight: 400,
                  fontSize: "22px",
                  color: "#2A4876",
                  fontFamily: "PT Sans, sans-serif !important",
                }}
              >
                -${formatPrice(priceData?.discount / 100, true)}
              </Typography>
            </div>
          )}
          <div
            className="flex justify-between items-center"
            style={{ color: "#2A4876", marginBottom: "8px" }}
          >
            <Typography
              variant="body"
              sx={{
                fontWeight: 600,
                fontSize: "24px",
                color: "#2A4876",
                fontFamily: "PT Sans, sans-serif !important",
              }}
            >
              Total Amount
            </Typography>
            <Typography
              variant="body"
              sx={{
                fontWeight: 600,
                fontSize: "24px",
                color: "#2A4876",
                fontFamily: "PT Sans, sans-serif !important",
              }}
            >
              ${finalTotal}
            </Typography>
          </div>
          {/* Card Form */}
          <Box>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: "16px",
                color: "#2A4876",
                textAlign: "left",
                marginBottom: "5px",
                fontFamily: "PT Sans, sans-serif !important",
              }}
            >
              Enter Card Holder&apos;s Name
            </Typography>
            <RoundedTextField
              fullWidth
              value={values.cardholderName}
              onChange={(e) => handleChange("cardholderName", e.target.value)}
              placeholder="Enter here"
              className="card-holder-name"
              sx={{
                "& .MuiOutlinedInput-root": {
                  padding: "0 !important",
                },
                "& .MuiInputBase-input": {
                  fontSize: "16px",
                  color: "#2A4876",
                  padding: "10px 15px",
                },
              }}
            />
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: "16px",
                color: "#2A4876",
                textAlign: "left",
                marginBottom: "5px",
                fontFamily: "PT Sans, sans-serif !important",
              }}
              mt={2}
            >
              Enter Card Number
            </Typography>
            <CardNumberElement
              options={createOptions()}
              className="input-box stripe"
              onChange={handleStripeChange("cardNumber")}
            />
            <Box display="flex" gap={2} mt={2}>
              <Box flex={1}>
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: "16px",
                    color: "#2A4876",
                    textAlign: "left",
                    marginBottom: "5px",
                    fontFamily: "PT Sans, sans-serif !important",
                  }}
                >
                  Enter Expiry Date
                </Typography>
                <CardExpiryElement
                  options={createOptions()}
                  className="input-box stripe"
                  onChange={handleStripeChange("expiry")}
                />
              </Box>
              <Box flex={1}>
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: "16px",
                    color: "#2A4876",
                    textAlign: "left",
                    marginBottom: "5px",
                    fontFamily: "PT Sans, sans-serif !important",
                  }}
                >
                  Enter CVV
                </Typography>
                <CardCvcElement
                  options={createOptions()}
                  className="input-box stripe"
                  onChange={handleStripeChange("cvv")}
                />
              </Box>
            </Box>
          </Box>
          {subsError && (
            <Typography color="error" mt={2}>
              {subsError}
            </Typography>
          )}
          <Button
            fullWidth
            variant="contained"
            sx={{
              textTransform: "none",
              backgroundColor: "#2A4876", // Change color conditionally
              color: "#fff",
              marginTop: "20px !important",
              height: "60px",
            }}
            className="Send-button"
            disabled={
              !values.cardholderName ||
              !values.cardNumber ||
              !values.cvv ||
              !values.expiry
            }
            onClick={handleSubmit}
          >
            Pay Now
          </Button>
        </Box>
      )}
      {/* Modals */}
      <SuccessModal
        open={isOpen}
        title="Welcome to the Echo Community!"
        subTitle="Thanks for your payment. You can now explore our services."
        onClose={() => {
          setIsOpen(false);
          navigate("/dashboard");
        }}
        register={true}
      />

      {openPlansModal && (
        <PlanSelectionModal
          activePack={activePack}
          openPlansModal={openPlansModal}
          handleCancel={() => setOpenPlansModal(false)}
          selectedPackage={selectedPackage}
          selectedPlan={selectedPlan}
          setSelectedPlan={setSelectedPlan}
          handleConfirm={() => {
            couponRequest.current?.abort();
            setPriceData(null);
            setDiscountCode("");
            setDiscountClicked(false);
            setOpenPlansModal(false);
          }}
        />
      )}
      <DiscountApplyModal
        open={openDiscountModal}
        onClose={handleDiscountClose}
      />
      <CopyRightTextMain />
    </>
  );
};

export default EditPaymentCard;
